import React, { ReactElement, useEffect, useState } from 'react';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import { Button, LoaderButton } from 'idsk-react-components';
import {
  useClient,
  useClientFetch,
  useClientStatusContext,
} from '../../../../../../context/ClientStatusContext';
import PracovnyKrokRequest from '../../../../../../model/pracovnyKrok/PracovnyKrokRequest';
import splitCisloLegMaterialu from '../../../../../../function/splitCisloLegMaterialu';
import LegislativnyMaterialItem
  from '../../../../../../model/pracovnyKrok/LegislativnyMaterialItem';
import {
  fetchPripomienkyValidation,
} from '../../../../../../client/external/pripomienky/pripomienkyValidationClient';
import { useSuccessMessageContext } from '../../../../../../context/SuccessMessageContext';
import {
  usePracovnyKrokLegislativnyMaterialContext,
} from '../../../../../../context/PracovnyKrokLegislativnyMaterialContext';
import {
  cancelVyhodnotenieMpk,
} from '../../../../../../client/pracovnyKrok/ukoncenieVyhodnoteniaMpk';

interface Props {
  legMaterial: LegislativnyMaterialItem;
  pracovnyKrok: PracovnyKrokRequest;
  reloadDetail?: () => void;
  reloadPracovneKroky?: () => void;
  aktEu?: boolean;
  setIsDropdownOpened: (isDropdownOpened: boolean) => void,
}

const UkoncitVyhodnotenieMpk = ({
  legMaterial,
  pracovnyKrok,
  reloadDetail,
  reloadPracovneKroky,
  aktEu = false,
  setIsDropdownOpened,
}: Props): ReactElement => {
  const {
    clearMessages,
    displaySuccess,
  } = useSuccessMessageContext();
  const { clearStatus } = useClientStatusContext();
  const {
    setMaterial,
    setLoadingMaterial,
  } = usePracovnyKrokLegislativnyMaterialContext();
  const {
    type,
    year,
    index,
  } = splitCisloLegMaterialu(legMaterial.cisloLegislativnehoMaterialu);

  const {
    data: pripomienkaValidationResponse,
    refetch: reloadPripomienkyValidationResponse,
  } = useClientFetch(
    fetchPripomienkyValidation,
    [type, year, index],
    [],
  );

  const {
    wrapClient: cancelVyhodnotenieMpkClient,
    status: cancelVyhodnotenieMpkStatus,
  } = useClient(cancelVyhodnotenieMpk);

  const ukoncitVyhodnotenieMpk = async (): Promise<void> => {
    await cancelVyhodnotenieMpkClient(
      type,
      year,
      index,
      pracovnyKrok.pracovnyKrokId,
      legMaterial.cisloLegislativnehoMaterialu,
      pracovnyKrok.pracovnyKrok,
    );

    if (reloadDetail !== undefined) {
      reloadDetail();
    }

    if (reloadPracovneKroky !== undefined) {
      reloadPracovneKroky();
    }

    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  useEffect(
    () => setMaterial(legMaterial),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [legMaterial?.uuid, legMaterial?.typ,
      legMaterial?.cisloLegislativnehoMaterialu, setMaterial],
  );

  useEffect(() => {
    setLoadingMaterial(cancelVyhodnotenieMpkStatus?.loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelVyhodnotenieMpkStatus?.loading]);

  const descriptionMessageStadium = aktEu ? 'Vytvorenie finálneho predbežného stanoviska'
    : 'Spracovanie predkladateľom';

  useEffect(() => {
    if (pripomienkaValidationResponse && !pripomienkaValidationResponse?.nezasadnaValidation) {
      setConfirmActionDescription('Niektoré pripomienky k materiálu, ktoré nie sú zásadné, neboli vyhodnotené. '
          + 'Po vykonaní tohto pracovného kroku sa spustí nasledujúce štádium a nebude možné vykonávať pracovné kroky '
          + 'dostupné v aktuálnom štádiu.');
    } else {
      setConfirmActionDescription('Po vykonaní tohto pracovného kroku sa spustí štádium '
          + `"${descriptionMessageStadium}" a nebude možné vykonávať pracovné kroky dostupné v aktuálnom štádiu.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pripomienkaValidationResponse]);

  const clearStatusMessagesAndReloadValidation = (): void => {
    clearStatus();
    clearMessages();
    reloadPripomienkyValidationResponse();
  };

  const [confirmActionDescription, setConfirmActionDescription] = useState<string>('');

  return (
    <ConfirmModal
      question={'Naozaj si prajete ukončiť štádium "Vyhodnotenie pripomienkového konania"?'}
      description={confirmActionDescription}
      cancelButtonText="Nie"
      confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
      openButton={(
        <LoaderButton
          className="pracovny-krok__button"
          buttonStyle="link"
          size="small"
          onClick={() => {
            clearStatusMessagesAndReloadValidation();
            setIsDropdownOpened(false);
          }}
          loading={cancelVyhodnotenieMpkStatus.loading}
          disableOutsideClickWhileLoading
        >
          {pracovnyKrok.nazov}
        </LoaderButton>
          )}
      doAfterConfirm={ukoncitVyhodnotenieMpk}
    />
  );
};

export default UkoncitVyhodnotenieMpk;
